import { TagDAO } from './dao/TagDAO.js';
import { BlogDAO } from './dao/BlogDAO.js';

export class TagService {
  constructor() {
    this.blogDAO = new BlogDAO();
    this.tagDAO = new TagDAO(this.blogDAO);
  }

  // CORE TAG OPERATIONS

  // Get all available tags with usage statistics
  async getAllTags(options = {}) {
    const { 
      includeStats = false, 
      minUsage = 0, 
      sortBy = 'name', 
      sortOrder = 'asc',
      limit = null 
    } = options;

    try {
      if (includeStats) {
        const tagStats = await this.tagDAO.getTagStats();
        
        let filteredTags = minUsage > 0 
          ? tagStats.filter(tag => tag.count >= minUsage)
          : tagStats;

        // Sort by specified criteria
        filteredTags.sort((a, b) => {
          let comparison = 0;
          
          switch (sortBy) {
            case 'name':
              comparison = a.name.localeCompare(b.name);
              break;
            case 'count':
              comparison = a.count - b.count;
              break;
            case 'index':
              comparison = a.index - b.index;
              break;
            default:
              comparison = a.name.localeCompare(b.name);
          }
          
          return sortOrder === 'desc' ? -comparison : comparison;
        });

        return limit ? filteredTags.slice(0, limit) : filteredTags;
      } else {
        // Simple tag list without stats
        const allTags = await this.tagDAO.getAllTags();
        const tagList = allTags.map((name, index) => ({ name, index }));
        
        tagList.sort((a, b) => {
          const comparison = a.name.localeCompare(b.name);
          return sortOrder === 'desc' ? -comparison : comparison;
        });

        return limit ? tagList.slice(0, limit) : tagList;
      }
    } catch (error) {
      console.error('Failed to get all tags:', error);
      return [];
    }
  }

  // Get popular tags (most frequently used)
  async getPopularTags(limit = 20) {
    try {
      return await this.tagDAO.getPopularTags(limit);
    } catch (error) {
      console.error('Failed to get popular tags:', error);
      return [];
    }
  }

  // Get tag cloud data for visualization
  async getTagCloud(options = {}) {
    const { minCount = 2, maxTags = 50, weightAlgorithm = 'linear' } = options;

    try {
      const tagCloud = await this.tagDAO.getTagCloud(minCount, maxTags);
      
      // Apply different weighting algorithms
      if (weightAlgorithm === 'logarithmic') {
        return tagCloud.map(tag => ({
          ...tag,
          weight: Math.log(tag.count + 1) / Math.log(Math.max(...tagCloud.map(t => t.count)) + 1),
          fontSize: Math.max(0.8, Math.min(2, 0.8 + Math.log(tag.count + 1) * 0.3))
        }));
      }
      
      return tagCloud;
    } catch (error) {
      console.error('Failed to get tag cloud:', error);
      return [];
    }
  }

  // Get related tags for a given tag
  async getRelatedTags(tagName, options = {}) {
    const { limit = 10, minCoOccurrence = 1 } = options;

    try {
      const relatedTags = await this.tagDAO.getRelatedTags(tagName, limit * 2); // Get more for filtering
      
      return relatedTags
        .filter(tag => tag.count >= minCoOccurrence)
        .slice(0, limit);
    } catch (error) {
      console.error(`Failed to get related tags for ${tagName}:`, error);
      return [];
    }
  }

  // Search tags by name
  async searchTags(query, options = {}) {
    const { limit = 20, includeStats = false } = options;
    
    if (!query || query.trim().length < 1) return [];

    try {
      const searchResults = await this.tagDAO.searchTags(query, limit);
      
      if (includeStats) {
        const tagStats = await this.tagDAO.getTagStats();
        const statsMap = new Map(tagStats.map(stat => [stat.name, stat]));
        
        return searchResults.map(tag => ({
          ...tag,
          count: statsMap.get(tag.name)?.count || 0
        }));
      }
      
      return searchResults;
    } catch (error) {
      console.error(`Failed to search tags for "${query}":`, error);
      return [];
    }
  }

  // TAG-BASED CONTENT OPERATIONS

  // Get stories by tag name
  async getStoriesByTag(tagName, options = {}) {
    const { 
      limit = null, 
      offset = 0, 
      includeUnpublished = false,
      sortBy = 'date',
      sortOrder = 'desc'
    } = options;

    try {
      // First check if tag exists
      const tagExists = await this.tagDAO.tagExists(tagName);
      if (!tagExists) {
        return { stories: [], pagination: { total: 0, limit: 0, offset: 0, hasMore: false } };
      }

      // Get basic story references by tag
      const storyRefs = await this.tagDAO.getStoriesByTagName(tagName);
      
      // Load full story data
      const stories = [];
      
      for (const storyRef of storyRefs) {
        if (!includeUnpublished && 
            !this.blogDAO.shouldShow(storyRef.publishDate, 'published')) {
          continue;
        }

        // Load story metadata
        const storyData = await this.blogDAO.getStoryMetadata(storyRef.id, null, true);
        if (storyData) {
          const blogDb = await this.blogDAO.getBlogDatabase();
          const story = blogDb.stories.find(s => s.id === storyRef.id);
          
          if (story) {
            const slug = this.blogDAO.findRoutingKey(blogDb.routing, story.id);
            const tagNames = await this.tagDAO.getTagNames(story.tags || []);

            stories.push({
              id: story.id,
              slug,
              title: storyData.title,
              excerpt: storyData.excerpt,
              publishDate: story.publish_date,
              category: story.category,
              tags: tagNames,
              readTime: storyData.readTime,
              coverImage: storyData.coverImage,
              featured: story.featured,
              author: {
                slug: story.author_slug,
                name: storyData.author
              },
              url: `/blog/${slug}`
            });
          }
        }
      }

      // Sort stories
      stories.sort((a, b) => {
        let comparison = 0;
        
        switch (sortBy) {
          case 'date':
            comparison = new Date(a.publishDate) - new Date(b.publishDate);
            break;
          case 'title':
            comparison = a.title.localeCompare(b.title);
            break;
          case 'category':
            comparison = a.category.localeCompare(b.category);
            break;
          case 'readTime':
            comparison = (a.readTime || 0) - (b.readTime || 0);
            break;
          default:
            comparison = new Date(a.publishDate) - new Date(b.publishDate);
        }
        
        return sortOrder === 'desc' ? -comparison : comparison;
      });

      // Apply pagination
      const total = stories.length;
      const paginatedStories = limit || offset 
        ? stories.slice(offset, limit ? offset + limit : undefined)
        : stories;

      return {
        stories: paginatedStories,
        pagination: {
          total,
          limit: limit || total,
          offset,
          hasMore: limit ? offset + limit < total : false
        },
        tag: {
          name: tagName,
          count: total
        }
      };
    } catch (error) {
      console.error(`Failed to get stories by tag ${tagName}:`, error);
      return { 
        stories: [], 
        pagination: { total: 0, limit: 0, offset: 0, hasMore: false },
        tag: { name: tagName, count: 0 }
      };
    }
  }

  // Get tags grouped by category
  async getTagsByCategory(options = {}) {
    const { minTagCount = 1, maxTagsPerCategory = 20 } = options;

    try {
      const tagsByCategory = await this.tagDAO.getTagsByCategory();
      
      return tagsByCategory.map(categoryData => ({
        category: categoryData.category,
        tags: categoryData.tags
          .filter(tag => tag.count >= minTagCount)
          .slice(0, maxTagsPerCategory)
      })).filter(categoryData => categoryData.tags.length > 0);
    } catch (error) {
      console.error('Failed to get tags by category:', error);
      return [];
    }
  }

  // ADVANCED TAG ANALYSIS

  // Get tag trends (comparing current vs previous period)
  async getTagTrends(options = {}) {
    const { period = 90, minStories = 2 } = options; // 90 days default

    try {
      const blogDb = await this.blogDAO.getBlogDatabase();
      const currentDate = new Date();
      const periodStart = new Date(currentDate.getTime() - period * 24 * 60 * 60 * 1000);
      const previousPeriodStart = new Date(periodStart.getTime() - period * 24 * 60 * 60 * 1000);

      const tagStats = {
        current: {},
        previous: {}
      };

      // Analyze stories in both periods
      blogDb.stories.forEach(story => {
        if (!story.tags || !story.publish_date) return;
        
        const publishDate = new Date(story.publish_date);
        let periodKey = null;

        if (publishDate >= periodStart && publishDate <= currentDate) {
          periodKey = 'current';
        } else if (publishDate >= previousPeriodStart && publishDate < periodStart) {
          periodKey = 'previous';
        }

        if (periodKey) {
          story.tags.forEach(tagIndex => {
            const tagName = blogDb.tags[tagIndex];
            if (tagName) {
              tagStats[periodKey][tagName] = (tagStats[periodKey][tagName] || 0) + 1;
            }
          });
        }
      });

      // Calculate trends
      const allTags = new Set([
        ...Object.keys(tagStats.current),
        ...Object.keys(tagStats.previous)
      ]);

      const trends = Array.from(allTags).map(tagName => {
        const currentCount = tagStats.current[tagName] || 0;
        const previousCount = tagStats.previous[tagName] || 0;
        const totalCount = currentCount + previousCount;

        if (totalCount < minStories) return null;

        let trend = 'stable';
        let change = 0;

        if (previousCount === 0 && currentCount > 0) {
          trend = 'new';
          change = 100;
        } else if (previousCount > 0) {
          change = Math.round(((currentCount - previousCount) / previousCount) * 100);
          
          if (change > 25) trend = 'rising';
          else if (change < -25) trend = 'declining';
        }

        return {
          tag: tagName,
          currentCount,
          previousCount,
          totalCount,
          trend,
          changePercentage: change
        };
      }).filter(Boolean);

      return trends.sort((a, b) => b.totalCount - a.totalCount);
    } catch (error) {
      console.error('Failed to get tag trends:', error);
      return [];
    }
  }

  // Get tag usage patterns across different content types
  async getTagUsagePatterns() {
    try {
      const blogDb = await this.blogDAO.getBlogDatabase();
      const patterns = {
        byType: { standalone: {}, series: {} },
        byCategory: {},
        byAuthor: {},
        byReadTime: { short: {}, medium: {}, long: {} }
      };

      // Analyze each story
      for (const story of blogDb.stories) {
        if (!story.tags || story.tags.length === 0) continue;

        // Get story metadata for additional analysis
        const storyData = await this.blogDAO.getStoryMetadata(story.id, null, true);
        const readTime = storyData?.readTime || 0;

        story.tags.forEach(tagIndex => {
          const tagName = blogDb.tags[tagIndex];
          if (!tagName) return;

          // By type
          patterns.byType[story.type][tagName] = (patterns.byType[story.type][tagName] || 0) + 1;

          // By category
          if (!patterns.byCategory[story.category]) {
            patterns.byCategory[story.category] = {};
          }
          patterns.byCategory[story.category][tagName] = 
            (patterns.byCategory[story.category][tagName] || 0) + 1;

          // By author
          if (story.author_slug) {
            if (!patterns.byAuthor[story.author_slug]) {
              patterns.byAuthor[story.author_slug] = {};
            }
            patterns.byAuthor[story.author_slug][tagName] = 
              (patterns.byAuthor[story.author_slug][tagName] || 0) + 1;
          }

          // By read time
          let timeCategory = 'medium';
          if (readTime <= 3) timeCategory = 'short';
          else if (readTime >= 10) timeCategory = 'long';

          patterns.byReadTime[timeCategory][tagName] = 
            (patterns.byReadTime[timeCategory][tagName] || 0) + 1;
        });
      }

      // Convert to sorted arrays
      const formatPattern = (patternObj) => 
        Object.entries(patternObj)
          .map(([name, count]) => ({ name, count }))
          .sort((a, b) => b.count - a.count);

      return {
        byType: {
          standalone: formatPattern(patterns.byType.standalone),
          series: formatPattern(patterns.byType.series)
        },
        byCategory: Object.entries(patterns.byCategory).map(([category, tags]) => ({
          category,
          tags: formatPattern(tags).slice(0, 10) // Top 10 per category
        })),
        byReadTime: {
          short: formatPattern(patterns.byReadTime.short).slice(0, 10),
          medium: formatPattern(patterns.byReadTime.medium).slice(0, 10),
          long: formatPattern(patterns.byReadTime.long).slice(0, 10)
        }
      };
    } catch (error) {
      console.error('Failed to get tag usage patterns:', error);
      return {
        byType: { standalone: [], series: [] },
        byCategory: [],
        byReadTime: { short: [], medium: [], long: [] }
      };
    }
  }

  // UTILITY METHODS

  // Convert tag names to indices
  async getTagIndices(tagNames) {
    try {
      return await this.tagDAO.getTagIndices(tagNames);
    } catch (error) {
      console.error('Failed to convert tag names to indices:', error);
      return [];
    }
  }

  // Convert tag indices to names
  async getTagNames(tagIndices) {
    try {
      return await this.tagDAO.getTagNames(tagIndices);
    } catch (error) {
      console.error('Failed to convert tag indices to names:', error);
      return [];
    }
  }

  // Check if tag exists
  async tagExists(tagName) {
    try {
      return await this.tagDAO.tagExists(tagName);
    } catch (error) {
      console.error(`Failed to check if tag exists: ${tagName}`, error);
      return false;
    }
  }

  // Get tag statistics
  async getTagStats(tagName = null) {
    try {
      const allStats = await this.tagDAO.getTagStats();
      
      if (tagName) {
        return allStats.find(stat => stat.name === tagName) || null;
      }
      
      return allStats;
    } catch (error) {
      console.error('Failed to get tag statistics:', error);
      return tagName ? null : [];
    }
  }

  // Calculate tag similarity based on co-occurrence
  async getTagSimilarity(tagName1, tagName2) {
    try {
      const blogDb = await this.blogDAO.getBlogDatabase();
      const tag1Index = blogDb.tags.indexOf(tagName1);
      const tag2Index = blogDb.tags.indexOf(tagName2);
      
      if (tag1Index === -1 || tag2Index === -1) return 0;

      let coOccurrence = 0;
      let tag1Count = 0;
      let tag2Count = 0;

      blogDb.stories.forEach(story => {
        if (!story.tags) return;
        
        const hasTag1 = story.tags.includes(tag1Index);
        const hasTag2 = story.tags.includes(tag2Index);
        
        if (hasTag1) tag1Count++;
        if (hasTag2) tag2Count++;
        if (hasTag1 && hasTag2) coOccurrence++;
      });

      // Jaccard similarity coefficient
      const union = tag1Count + tag2Count - coOccurrence;
      return union === 0 ? 0 : coOccurrence / union;
    } catch (error) {
      console.error(`Failed to calculate tag similarity: ${tagName1}, ${tagName2}`, error);
      return 0;
    }
  }

  // Get recommended tags for content (based on title/category/existing tags)
  async getRecommendedTags(options = {}) {
    const { 
      title = '', 
      category = '', 
      existingTags = [], 
      limit = 10 
    } = options;

    try {
      const allTagStats = await this.tagDAO.getTagStats();
      const recommendations = [];

      // Get category-specific tags
      const categoryTags = await this.getTagsByCategory();
      const categoryData = categoryTags.find(cat => cat.category === category);
      
      if (categoryData) {
        categoryData.tags.forEach(tag => {
          if (!existingTags.includes(tag.name)) {
            recommendations.push({
              tag: tag.name,
              score: tag.count * 2, // Higher weight for category relevance
              reason: 'category-popular'
            });
          }
        });
      }

      // Keyword matching in title
      const titleWords = title.toLowerCase().split(/\s+/);
      allTagStats.forEach(tagStat => {
        const tagWords = tagStat.name.toLowerCase().split(/\s+/);
        const hasMatch = tagWords.some(word => 
          titleWords.some(titleWord => 
            titleWord.includes(word) || word.includes(titleWord)
          )
        );
        
        if (hasMatch && !existingTags.includes(tagStat.name)) {
          recommendations.push({
            tag: tagStat.name,
            score: tagStat.count * 3, // Highest weight for title relevance
            reason: 'title-match'
          });
        }
      });

      // Related tags based on existing tags
      for (const existingTag of existingTags) {
        const relatedTags = await this.getRelatedTags(existingTag, { limit: 5 });
        relatedTags.forEach(related => {
          if (!existingTags.includes(related.name)) {
            const existing = recommendations.find(r => r.tag === related.name);
            if (existing) {
              existing.score += related.count;
            } else {
              recommendations.push({
                tag: related.name,
                score: related.count,
                reason: 'tag-relation'
              });
            }
          }
        });
      }

      // Sort by score and return top recommendations
      return recommendations
        .sort((a, b) => b.score - a.score)
        .slice(0, limit)
        .map(rec => ({
          tag: rec.tag,
          confidence: Math.min(1, rec.score / 100), // Normalize to 0-1
          reason: rec.reason
        }));
    } catch (error) {
      console.error('Failed to get recommended tags:', error);
      return [];
    }
  }
}